import type { InputT } from "@patient-watch/types";

import { toOwnOptionRow, type OwnOptionT } from "@/features/dictionaries/ownOptions";

/**
 * An options_from input limited to the choices picked earlier (`allowedCodes`), plus the respondent's own
 * options picked earlier (`ownOptions`), which follow as themselves. Dictionary choices are matched on their
 * code and the input's own choices on their value; the allowed order is the dictionary's. Inputs without a
 * limit are returned unchanged.
 */
export function restrictChoices(
    input: InputT,
    allowedCodes: readonly number[] | undefined,
    ownOptions: readonly OwnOptionT[] = []
): InputT {
    if (!allowedCodes) return input;
    const allowed = new Set(allowedCodes.map(String));
    const optionRows = input.option_rows?.filter((row) => row.code != null && allowed.has(String(row.code))) ?? null;
    const scaleOptions = (input.scale_options ?? []).filter((option) => allowed.has(String(option.value)));
    if (ownOptions.length === 0) return { ...input, option_rows: optionRows, scale_options: scaleOptions };
    // Choices from option rows are rebuilt from the rows; an input without rows reads scale_options.
    return input.option_rows
        ? {
              ...input,
              option_rows: [...(optionRows ?? []), ...ownOptions.map((option, index) => toOwnOptionRow(option, index))],
              scale_options: scaleOptions,
          }
        : {
              ...input,
              option_rows: optionRows,
              scale_options: [
                  ...scaleOptions,
                  ...ownOptions.map((option) => ({ label: option.label, value: option.option_id })),
              ] as InputT["scale_options"],
          };
}
